import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { MailCheck, Loader2, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { api, errMsg, fmtDateTime } from "@/lib/api";
import { useFetch } from "@/lib/hooks";
import { Panel } from "@/components/core/AppLayout";
import { openBlob } from "@/lib/invoices";

const ESITO = { inviati: ["Inviato", "bg-emerald-50 text-emerald-800 border-emerald-200"], errori: ["Errore", "bg-red-50 text-[#C8102E] border-red-200"],
  duplicati: ["Già inviato", "bg-slate-50 text-slate-700 border-slate-200"] };

const useLinkDownload = () => {
  const [sp, setSp] = useSearchParams();
  useEffect(() => {
    const id = sp.get("backup");
    if (!id) return;
    setSp({}, { replace: true });
    api.get(`/admin/backup/file/${id}`, { responseType: "blob" })
      .then((r) => { openBlob(r.data, "core-backup.zip", true); toast.success("Download del backup avviato"); })
      .catch((e) => toast.error(e.response?.status === 410 ? "Il link di download è scaduto" : "Backup non disponibile"));
  }, [sp, setSp]);
};

export const WeeklyBackupPanel = ({ onChanged }) => {
  useLinkDownload();
  const { data, setData } = useFetch("/admin/backup/settimanale");
  const [busy, setBusy] = useState(false);
  const toggle = async (attivo) => {
    try { setData((await api.put("/admin/backup/settimanale", { attivo })).data); toast.success(attivo ? "Backup settimanale attivato" : "Backup settimanale disattivato"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const sendNow = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/backup/invia-ora");
      toast.success(`Backup inviato a ${r.data.destinatario}` + (r.data.modo === "link" ? " (con link di download)" : " (in allegato)"));
      setData((d) => ({ ...d, ultimo: r.data })); onChanged?.();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  const u = data?.ultimo;
  return (
    <Panel className="p-6 mb-6" data-testid="weekly-backup-panel">
      <div className="flex items-start justify-between gap-6">
        <div>
          <h3 className="font-display text-lg font-extrabold text-[#0A1128] flex items-center gap-2 mb-1"><MailCheck className="h-5 w-5 text-[#E8173A]" />Backup settimanale via email</h3>
          <p className="text-sm text-slate-700 max-w-2xl">Ogni domenica alle 21:00 (ora di Roma) l'amministratore riceve il backup ZIP in allegato: senza password degli accessi e con solo i propri appunti privati. Se il file supera ~25 MB arriva un link di download sicuro (solo amministratore, valido 7 giorni).</p>
        </div>
        {data && <label className="flex items-center gap-2 text-sm font-bold text-[#0A1128] shrink-0"><Switch checked={data.attivo} onCheckedChange={toggle} data-testid="weekly-backup-toggle" />{data.attivo ? "Attivo" : "Disattivato"}</label>}
      </div>
      <div className="flex items-center justify-between gap-4 mt-4 rounded-xl bg-[#A6DAFC] px-4 py-3">
        <div className="text-sm text-[#0A1128]" data-testid="weekly-backup-status">
          {u ? <>Ultimo backup: <strong>{fmtDateTime(u.at)}</strong> · {u.modo === "link" ? "link di download" : "allegato"} · {(u.size / 1024 / 1024).toFixed(1)} MB{" "}
            <span className={`ml-1 text-[11px] font-bold rounded-full border px-2 py-0.5 ${(ESITO[u.esito] || ESITO.errori)[1]}`}>{(ESITO[u.esito] || ESITO.errori)[0]}</span>
            {u.errore && <span className="block text-xs text-[#C8102E] mt-0.5">{u.errore}</span>}</> : "Nessun backup inviato finora."}
          {data?.prossima_esecuzione && data.attivo && <span className="block text-xs text-slate-600 mt-0.5">Prossimo invio: {fmtDateTime(data.prossima_esecuzione)}</span>}
        </div>
        <Button onClick={sendNow} disabled={busy} className="rounded-full bg-[#0A1128] hover:bg-[#E8173A] font-bold shrink-0" data-testid="weekly-backup-send-now">
          {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Send className="h-4 w-4 mr-1.5" />}Invia backup ora</Button>
      </div>
    </Panel>
  );
};
